import { STANDARDISED_AMENITIES_MAP } from 'constants/amenities'

function boostByPredicate<T>(
  items: Array<T>,
  shouldBoost: (item: T) => boolean,
): Array<T> {
  const reorderedItems = items.reduce<{
    boosted: Array<T>
    rest: Array<T>
  }>((reorderedItems, item) => {
    if (shouldBoost(item)) {
      reorderedItems.boosted.push(item)
    } else {
      reorderedItems.rest.push(item)
    }

    return reorderedItems
  }, { boosted: [], rest: [] })

  return [...reorderedItems.boosted, ...reorderedItems.rest]
}

/**
 * Reorders amenities/popular facilities by boosting those that map to an active amenity filter.
 * Uses STANDARDISED_AMENITIES_MAP to translate facility description to standardized category.
 * Non-matching amenities maintain their original relative order.
 *
 * @param amenities - Array of OfferInclusions to reorder (has description field from popularFacilities)
 * @param activeAmenities - Set of amenity category strings to boost (e.g., Set(['Private pool', 'Coffee machine']))
 * @returns New array with matching amenities boosted to the top
 */
export function reorderAmenitiesByActiveFilters(
  amenities: Array<string>,
  activeAmenities: Set<string>,
): Array<string> {
  if (!activeAmenities.size || !amenities.length) {
    return amenities
  }

  return boostByPredicate(amenities, (amenity) => {
    const amenityCategory = amenity ? STANDARDISED_AMENITIES_MAP[amenity] : undefined
    return !!amenityCategory && activeAmenities.has(amenityCategory)
  })
}

export function reorderDescribedAmenitiesByActiveFilters<T extends { description?: string }>(
  amenities: Array<T>,
  activeAmenities: Set<string>,
): Array<T> {
  if (!activeAmenities.size || !amenities.length) {
    return amenities
  }

  return boostByPredicate(amenities, (amenity) => {
    const amenityCategory = amenity.description ? STANDARDISED_AMENITIES_MAP[amenity.description] : undefined
    return !!amenityCategory && activeAmenities.has(amenityCategory)
  })
}

export function reorderPropertyAmenitiesByActiveFilters(
  amenities: Array<App.Property.AmenityItem>,
  activeAmenities: Set<string>,
): Array<App.Property.AmenityItem> {
  if (!activeAmenities.size || !amenities.length) {
    return amenities
  }

  return boostByPredicate(amenities, (amenity) => {
    const amenityCategory = STANDARDISED_AMENITIES_MAP[amenity.name] ??
      STANDARDISED_AMENITIES_MAP[amenity.id]
    return !!amenityCategory && activeAmenities.has(amenityCategory)
  })
}
